import { Application, RenderTexture, Container, Assets } from 'pixi.js';
import { Spine } from 'pixi-spine';

export interface SkelAnimations {
  value: string;
  animation: string[];
}

export interface SkelItem {
  name: string;
  skelData: string;
  animations: SkelAnimations[];
}

export interface AnimationOptions {
  key: string;
  animations: string | string[];
  loop?: boolean;
  trackIndex?: number;
  initialDelay?: number;
}

export interface SingleAnimationOptions {
  animationName: string;
  loop?: boolean;
  trackIndex?: number;
  initialDelay?: number;
}

export interface ISpineRenderTextureManager {
  addAnimation: (options: AnimationOptions) => void;
  addSameAnimationToEachSpine: (options: SingleAnimationOptions) => void;
  getRenderTexture: (key: string) => RenderTexture | undefined;
  getSpine: (key: string) => Spine | undefined;
  destroy: () => void;
  clearAnimation: (key: string, trackIndex: number) => void;
  clearAllAnimations: (key: string) => void;
  clearAllAnimationsSpineAssets: () => void;
}

const WIDTH_GAP = 25;

export class SpineRenderTextureManager implements ISpineRenderTextureManager {
  private app: Application;
  private skelAssets: SkelItem[];
  private spineAssets: Map<string, Spine> = new Map();
  private renderTextures: Map<string, RenderTexture> = new Map();
  private containers: Map<string, Container> = new Map();

  /**
   * @param app - Instance PIXI.Application
   * @param skelAssets - skel assets
   */
  constructor(app: Application, skelAssets: SkelItem[]) {
    this.app = app;
    this.skelAssets = skelAssets;
    this.initSpineAssets();
  }

  private async initSpineAssets(): Promise<void> {
    this.skelAssets.map((item) => {
      Assets.add({ alias: item.name, src: item.skelData });
    });

    const keys = this.skelAssets.map((item) => item.name);
    await Assets.load(keys);

    for (let i = 0; i < this.skelAssets.length; i++) {
      try {
        const skelAsset = this.skelAssets[i];
        const spineKey = skelAsset.name;
        const asset = Assets.get(spineKey);
        const spine = new Spine(asset.spineData);

        this.spineAssets.set(spineKey, spine);

        const defaultAnimation = spine.spineData.animations[0].name;

        this.addAnimation({
          key: spineKey,
          animations: defaultAnimation,
          loop: true
        });

        const renderTexture = RenderTexture.create({
          width: spine.width + WIDTH_GAP,
          height: spine.height
        });

        this.renderTextures.set(spineKey, renderTexture);
        this.setupTicker(spine, renderTexture);
      } catch (error) {
        const skelAsset = this.skelAssets[i];
        const key = skelAsset.name;

        console.error(`Failed to load spine asset with key: ${key}:`, error);
      }
    }
  }

  /**
   *  Add RenderTexture to app renderer
   */
  private setupTicker(spine: Spine, renderTexture: RenderTexture): void {
    try {
      this.app.ticker.add(() => {
        this.app.renderer?.render(spine, {
          renderTexture
        });
      });
    } catch (error) {
      console.log(error);
    }
  }

  /**
   * Add single or multiple animations to Spine
   * @param key - key of Spine
   * @param animations - a single animation name or an array of animation names
   * @param loop - loop animation
   * @param trackIndex - track index
   * @param initialDelay - delay of animation
   */
  public addAnimation(options: AnimationOptions): void {
    const { key, animations, loop = false, trackIndex = 0, initialDelay = 0 } = options;

    const spine = this.spineAssets.get(key);

    if (!spine) {
      console.error(`Error adding animation. Spine asset with key ${key} not found`);
      return;
    }

    if (!Array.isArray(animations)) {
      spine.state.addAnimation(trackIndex, animations, loop, initialDelay);
      return;
    }

    animations.forEach((animationName, index) => {
      const isLast = index === animations.length - 1;

      let shouldLoop;

      if (index === 0) {
        shouldLoop = loop; // First animation used the loop from options
      } else if (isLast) {
        shouldLoop = loop; // Last animation used the loop from options
      } else {
        shouldLoop = false; // Intermediate animations don't loop
      }

      const currentDelay = index === 0 ? initialDelay : 0;
      spine.state.addAnimation(trackIndex, animationName, shouldLoop, currentDelay);
    });
  }

  /**
   * Add the same animation for each Spine
   * @param key - key of Spine
   * @param animationName - name of animation
   * @param loop - loop animation
   * @param trackIndex - track index
   */
  public addSameAnimationToEachSpine(options: SingleAnimationOptions): void {
    const { animationName, loop = false, trackIndex = 0, initialDelay = 0 } = options;

    if (!this.spineAssets.size) {
      console.warn('Warning on adding animations to spines. No Spine assets available for animation');
      return;
    }

    this.clearAllAnimationsSpineAssets();

    [...this.spineAssets.keys()].forEach((spineKey) => {
      this.addAnimation({
        key: spineKey,
        animations: animationName,
        loop,
        trackIndex,
        initialDelay
      });
    });
  }

  /**
   * Clear animation of Spine
   * @param key - key of Spine
   * @param trackIndex - track index
   */
  public clearAnimation(key: string, trackIndex: number = 0): void {
    const spine = this.spineAssets.get(key);

    if (!spine) {
      console.error(`Error clearing animation. Spine asset with key ${key} not found`);
      return;
    }

    spine.state.clearTrack(trackIndex);
  }

  /**
   * Clear all animation of Spine
   * @param key - key of Spine
   * @param trackIndex - track index
   */
  public clearAllAnimations(key: string): void {
    const spine = this.spineAssets.get(key);

    if (!spine) {
      console.error(`Error clearing animations of spine. Spine asset with key ${key} not found`);
      return;
    }

    spine.state.clearTracks();
  }

  /**
   * Clear all animations of Spine assets
   */
  public clearAllAnimationsSpineAssets(): void {
    if (!this.spineAssets.size) {
      console.warn('Warning on clear animations of spines. No Spine assets to clear');
      return;
    }

    this.spineAssets.forEach((spine) => {
      spine.state.clearTracks();
    });
  }

  /**
   * Get RenderTexture by key
   */
  public getRenderTexture(key: string): RenderTexture | undefined {
    return this.renderTextures.get(key);
  }

  /**
   * Get Spine by key
   */
  public getSpine(key: string): Spine | undefined {
    return this.spineAssets.get(key);
  }

  /**
   * Destroy all resources
   */
  public destroy(): void {
    this.spineAssets.forEach((spine) => spine.destroy());
    this.renderTextures.forEach((texture) => texture.destroy());
    this.containers.forEach((container) => container.destroy());

    this.spineAssets.clear();
    this.renderTextures.clear();
    this.containers.clear();
  }
}
